import { useEffect, useRef } from "react";
import { generatePath, Link, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight, Layers, Plus, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useGetCrmMasterDetail,
  type CrmMasterDetailTarget,
  type CrmRecordType,
} from "@/lib/api/crm/masters";
import { useGetCrmRules } from "@/lib/api/crm/rules";
import { cn } from "@/lib/utils";
import { RoutePath } from "../routerPaths";
import {
  recordTypeAccentClasses,
  recordTypeBadgeClasses,
  recordTypeIcons,
  recordTypeLabels,
  recordTypeOrder,
  recordTypePluralLabels,
} from "./recordTypes";
import { buildRecordHierarchy, descendantsOf, rootRecordOf } from "./recordHierarchy";
import { AUTH_USER_PARAM } from "./authUserForm";
import { CrmLoadError } from "./components/CrmLoadError";
import { DeleteMasterButton } from "./components/DeleteMasterButton";
import { recordElementId } from "./components/RecordCard";
import { RecordBranch, type AuthUserPanelTarget } from "./components/RecordBranch";

type DetailTab = CrmRecordType | "all";

/** Group page query param selecting a record type tab. */
const TYPE_PARAM = "type";

function typeCountLabel(type: CrmRecordType, count: number): string {
  return `${count} ${count === 1 ? recordTypeLabels[type] : recordTypePluralLabels[type]}`;
}

function DetailSkeleton() {
  return (
    <div className="min-h-full bg-slate-50">
      <div className="border-b bg-white">
        <div className="mx-auto max-w-[1400px] space-y-3 px-6 pt-6">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-9 w-80" />
          <div className="flex gap-2 pb-6">
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="h-6 w-24 rounded-full" />
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-[1400px] space-y-6 px-6 py-6">
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="ml-8 h-48 rounded-xl" />
      </div>
    </div>
  );
}

export function CrmDetailPage() {
  const { masterId, recordId } = useParams<{ masterId?: string; recordId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageRef = useRef<HTMLDivElement>(null);

  const target: CrmMasterDetailTarget = recordId ? { record_id: recordId } : { master_id: masterId ?? "" };
  const detailResult = useGetCrmMasterDetail(target, Boolean(recordId || masterId));
  const rulesResult = useGetCrmRules();

  const master = detailResult.data;
  const rules = rulesResult.data;
  const selectedRecordId = recordId && master?.records.some((record) => record.id === recordId)
    ? recordId
    : null;

  useEffect((): void => {
    if (selectedRecordId) {
      document.getElementById(recordElementId(selectedRecordId))?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (!recordId) {
      pageRef.current?.scrollIntoView({ block: "start" });
    }
  }, [selectedRecordId, recordId]);

  if (detailResult.isLoading || rulesResult.isLoading) {
    return <DetailSkeleton />;
  }

  if (!master || !rules) {
    return (
      <CrmLoadError
        subject={recordId ? "record" : "record group"}
        isRetrying={detailResult.isFetching || rulesResult.isFetching}
        onRetry={() => {
          if (!master) void detailResult.refetch();
          if (!rules) void rulesResult.refetch();
        }}
      />
    );
  }

  const groupPath = generatePath(RoutePath.CrmGroup, { masterId: master.id });
  const { recordsById, childrenByOwner, topLevel } = buildRecordHierarchy(master.records);

  // One tab per top-level record type (no owner per GET /crm/rules) present in the group, then "All".
  const tabTypes = recordTypeOrder.filter((type) =>
    rules.record_types[type].owner_types === null && topLevel.some((record) => record.record_type === type));
  const selectedRecord = selectedRecordId ? recordsById.get(selectedRecordId) : undefined;
  const selectedRoot = selectedRecord ? rootRecordOf(selectedRecord, recordsById) : undefined;
  const typeParam = searchParams.get(TYPE_PARAM);
  const activeTab: DetailTab = selectedRoot
    ? tabTypes.includes(selectedRoot.record_type) ? selectedRoot.record_type : "all"
    : tabTypes.find((type) => type === typeParam) ?? "all";

  // A selected record shows only its own chain; otherwise the tab's top-level records.
  const visibleRoots = selectedRoot
    ? [selectedRoot]
    : topLevel.filter((record) => activeTab === "all" || record.record_type === activeTab);
  const rootGroups = activeTab === "all" && !selectedRoot
    ? recordTypeOrder
      .map((type) => ({ type, records: visibleRoots.filter((record) => record.record_type === type) }))
      .filter((group) => group.records.length > 0)
    : [{ type: null, records: visibleRoots }];

  // The edited mobile login opens under the first shown record that lists it.
  const authUserId = searchParams.get(AUTH_USER_PARAM);
  const shownRecordIds = new Set(visibleRoots.flatMap((root) =>
    [root, ...descendantsOf(root.id, childrenByOwner)].map((record) => record.id)));
  const authUserRecord = authUserId
    ? master.records.find((record) =>
      shownRecordIds.has(record.id) && record.auth_users.some((authUser) => authUser.id === authUserId))
    : undefined;
  const authUserPanel: AuthUserPanelTarget | null = authUserId && authUserRecord
    ? {
      recordId: authUserRecord.id,
      authUserId,
      onClose: () => setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        next.delete(AUTH_USER_PARAM);
        return next;
      }),
    }
    : null;

  const typeCounts = recordTypeOrder
    .map((type) => ({ type, count: master.records.filter((record) => record.record_type === type).length }))
    .filter(({ count }) => count > 0);

  const tabs: { tab: DetailTab; label: string; count: number; to: string }[] = [
    { tab: "all", label: "All", count: master.records.length, to: groupPath },
    ...tabTypes.map((type) => ({
      tab: type,
      label: recordTypePluralLabels[type],
      count: topLevel.filter((record) => record.record_type === type).length,
      to: `${groupPath}?${new URLSearchParams({ [TYPE_PARAM]: type }).toString()}`,
    })),
  ];
  const activeTabLink = tabs.find(({ tab }) => tab === activeTab);
  // The viewed record (or one above it) becomes the new record's owner when its type allows.
  const newRecordPath = `${RoutePath.CrmNewRecord}?${new URLSearchParams({
    group: master.id,
    ...(selectedRecord ? { owner: selectedRecord.id } : {}),
  }).toString()}`;

  return (
    <div ref={pageRef} className="min-h-full bg-slate-50">
      <div className="border-b bg-white">
        <div className="mx-auto max-w-[1400px] px-6 pt-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-gray-500" aria-label="Breadcrumb">
            <Link to={RoutePath.CrmList} className="hover:text-gray-900 hover:underline">CRM</Link>
            <ChevronRight className="h-4 w-4 text-gray-300" />
            {selectedRecord ? (
              <>
                <Link to={groupPath} className="hover:text-gray-900 hover:underline">{master.long_name}</Link>
                <ChevronRight className="h-4 w-4 text-gray-300" />
                <span className="font-medium text-gray-900">{selectedRecord.long_name}</span>
              </>
            ) : (
              <span className="font-medium text-gray-900">{master.long_name}</span>
            )}
          </nav>

          <div className="mt-4 flex items-start gap-4 flex-col lg:flex-row">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gray-900 text-white shadow-sm">
              <Layers className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">Record Group</p>
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 lg:text-3xl" data-testid="crm-detail-title">
                {master.long_name}
              </h1>
              {typeCounts.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {typeCounts.map(({ type, count }) => (
                    <span
                      key={type}
                      className={cn(
                        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                        recordTypeBadgeClasses[type],
                      )}
                    >
                      {typeCountLabel(type, count)}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="ml-auto flex shrink-0 flex-wrap items-center justify-end gap-2">
              <DeleteMasterButton master={master} />
              <Button variant="outline" size="sm" asChild>
                <Link to={newRecordPath} data-testid="crm-detail-new-record">
                  <Plus className="mr-2 h-4 w-4" />
                  New record
                </Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { void detailResult.refetch(); }}
                disabled={detailResult.isFetching}
                data-testid="crm-detail-refresh"
              >
                <RefreshCw className={cn("mr-2 h-4 w-4", detailResult.isFetching && "animate-spin")} />
                Refresh
              </Button>
            </div>
          </div>

          <nav className="-mb-px mt-6 flex gap-1 overflow-x-auto" aria-label="Record types">
            {tabs.map(({ tab, label, count, to }) => {
              const isActive = tab === activeTab;
              const Icon = tab === "all" ? Layers : recordTypeIcons[tab];
              return (
                <Link
                  key={tab}
                  to={to}
                  className={cn(
                    "flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors",
                    isActive
                      ? "border-gray-900 text-gray-900"
                      : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800",
                  )}
                  aria-current={isActive ? "page" : undefined}
                  data-testid={`crm-tab-${tab}`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                  <span
                    className={cn(
                      "rounded-full px-1.5 py-px text-[11px] font-semibold",
                      isActive ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600",
                    )}
                  >
                    {count}
                  </span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] space-y-8 px-6 py-6">
        {selectedRoot && activeTabLink && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-white px-4 py-2.5 text-sm shadow-sm">
            <span className="text-gray-600">
              Showing <span className="font-medium text-gray-900">{selectedRoot.long_name}</span> and everything under it
            </span>
            <Link to={activeTabLink.to} className="font-medium text-blue-700 hover:underline" data-testid="crm-show-all">
              {activeTab === "all" ? "Show everything" : `Show all ${activeTabLink.label.toLowerCase()}`}
            </Link>
          </div>
        )}

        {master.records.length === 0 && (
          <div className="rounded-xl border border-dashed bg-white px-6 py-16 text-center text-sm text-gray-500">
            This group doesn't have any records yet.
          </div>
        )}

        {rootGroups.map(({ type, records }) => {
          const Icon = type ? recordTypeIcons[type] : null;
          return (
            <section key={type ?? "records"} className="space-y-6" data-testid={`crm-section-${type ?? activeTab}`}>
              {type && Icon && (
                <div className="flex items-center gap-3">
                  <span className={cn("flex h-8 w-8 items-center justify-center rounded-lg", recordTypeAccentClasses[type])}>
                    <Icon className="h-4 w-4" />
                  </span>
                  <h2 className="text-lg font-semibold text-gray-900">{recordTypePluralLabels[type]}</h2>
                  <span className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-600">
                    {records.length}
                  </span>
                </div>
              )}
              {records.map((record) => (
                <RecordBranch
                  key={record.id}
                  record={record}
                  childrenByOwner={childrenByOwner}
                  rules={rules}
                  selectedRecordId={selectedRecordId}
                  authUserPanel={authUserPanel}
                />
              ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
